import { useState, type ReactNode } from 'react';
import { Modal } from '../../components/Modal';
import { Avatar } from '../../components/Avatar';
import { Badge } from '../../components/Badge';
import { IconAlert, IconCalendar, IconRepeat, IconSwap } from '../../components/Icons';
import { useAdjustmentCandidates, useAdjustmentPreview, useApplyAdjustment } from '../../api/learning-plans';
import { learningPlanError, planLabel, weekdayNames } from './labels';
import type { LearningPlan, PlanAdjustmentInput, PlanAdjustmentLesson, PlanChange } from '../../types/learning-plans';

type Action = 'times' | 'teacher' | 'lesson';

const riyadh = (iso: string) => new Date(Date.parse(iso) + 3 * 3600000).toISOString();
const riyadhDate = (iso: string) => riyadh(iso).slice(0, 10);
const clock = (iso: string) => riyadh(iso).slice(11, 16);
const dayName = (iso: string) => weekdayNames[new Date(riyadh(iso)).getUTCDay()];
// Each purchased weekly lesson keeps its row while its day or time is edited.
const weeklyRows = (times?: { weekday: number; startLocal: string }[]) =>
  (times ?? []).map((t, row) => ({ row, weekday: t.weekday, startLocal: t.startLocal }));
const when = (iso: string) => `${dayName(iso)} ${riyadhDate(iso)} ${clock(iso)}`;

const ACTIONS: { value: Action; title: string; hint: string; icon: ReactNode }[] = [
  { value: 'times', title: 'تعديل المواعيد الأسبوعية', hint: 'أيام وأوقات جديدة من تاريخ معيّن', icon: <IconRepeat className="size-5" /> },
  { value: 'teacher', title: 'تغيير المعلّم', hint: 'معلّم أساسي جديد أو بديل لفترة', icon: <IconSwap className="size-5" /> },
  { value: 'lesson', title: 'تعديل حصة واحدة', hint: 'موعد آخر أو بديل لحصة بعينها', icon: <IconCalendar className="size-5" /> },
];

/** A selectable card: the radio stays real for keyboard and screen readers. */
export const optionCard =
  'relative flex cursor-pointer items-start gap-3 rounded-lg border border-rule bg-surface p-3 transition-colors hover:border-rule-strong ' +
  'has-[:checked]:border-accent has-[:checked]:bg-accent-wash has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent';

/**
 * Changing a running plan in one place: its weekly times, its teacher, or a
 * single lesson. Every change follows the calendar convention of "this lesson"
 * or "from this date on", shows each affected lesson before anything is saved,
 * and is applied in one step once the family's agreement is recorded. The
 * teacher is notified; nobody waits for an acceptance.
 */
export function PlanManager({
  request,
  change,
  onClose,
  onDone,
}: {
  request: LearningPlan;
  change?: PlanChange;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const tracks = request.offer.tracks.filter((t) => request.balances.some((b) => b.trackKey === t.key));
  const [trackKey, setTrackKey] = useState(change?.trackKey ?? tracks[0]?.key ?? '');
  // A trial is one lesson, so only that lesson can change.
  const actions = request.kind === 'trial' ? ACTIONS.filter((a) => a.value === 'lesson') : ACTIONS;
  const [action, setAction] = useState<Action>(
    request.kind === 'trial' ? 'lesson' : change?.kind === 'teacher_change' ? 'teacher' : 'times',
  );
  const track = tracks.find((t) => t.key === trackKey);
  const balance = request.balances.find((b) => b.trackKey === trackKey);
  const schedule = request.schedule?.find((s) => s.trackKey === trackKey);
  const upcoming = request.lessons.filter(
    (l) => l.trackKey === trackKey && l.status === 'confirmed' && Date.parse(l.startTime) > Date.now(),
  );
  const firstDate = upcoming[0] ? riyadhDate(upcoming[0].startTime) : riyadhDate(new Date().toISOString());
  const currentTimes = weeklyRows(schedule?.times);
  const [effectiveFrom, setEffectiveFrom] = useState(firstDate);
  const [times, setTimes] = useState(currentTimes);
  const [permanent, setPermanent] = useState(true);
  const [until, setUntil] = useState('');
  const [tutorId, setTutorId] = useState('');
  const [lessonId, setLessonId] = useState(upcoming[0]?.id ?? '');
  const [lessonStart, setLessonStart] = useState('');
  const [reason, setReason] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState('');
  const apply = useApplyAdjustment();
  // A family request is answered only by the change it asked for: new weekly
  // times, or a new regular teacher.
  const pendingChange = change?.status === 'pending' && change.trackKey === trackKey ? change : undefined;
  const scheduleChangeId = pendingChange?.kind === 'schedule' ? pendingChange.id : undefined;

  // The family agreed to one specific change; editing it withdraws that.
  const edit = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setAgreed(false);
    apply.reset();
  };
  const switchTrack = (key: string) => {
    const next = request.schedule?.find((s) => s.trackKey === key);
    setTrackKey(key);
    setTimes(weeklyRows(next?.times));
    setTutorId('');
    setLessonId(request.lessons.find((l) => l.trackKey === key && l.status === 'confirmed' && Date.parse(l.startTime) > Date.now())?.id ?? '');
    setAgreed(false);
  };

  const timesChanged = JSON.stringify(times) !== JSON.stringify(currentTimes);
  const lessonInput: PlanAdjustmentInput | null = lessonId
    ? { trackKey, scope: 'lesson', lessonId, startTime: lessonStart ? `${lessonStart}:00+03:00` : undefined, reason: 'preview' }
    : null;
  const rangeInput: PlanAdjustmentInput | null = effectiveFrom && (permanent || until)
    ? { trackKey, scope: 'following', effectiveFrom, until: permanent ? undefined : until, reason: 'preview' }
    : null;
  const draft: PlanAdjustmentInput | null =
    action === 'times'
      ? effectiveFrom && timesChanged && times.every((t) => t.startLocal)
        ? { trackKey, scope: 'following', effectiveFrom, times: times.map(({ weekday, startLocal }) => ({ weekday, startLocal })), changeId: scheduleChangeId, reason: 'preview' }
        : null
      : action === 'teacher'
        ? rangeInput && tutorId
          ? { ...rangeInput, tutorId, changeId: permanent && pendingChange?.kind === 'teacher_change' ? pendingChange.id : undefined }
          : null
        : lessonInput && (lessonStart || tutorId) ? { ...lessonInput, tutorId: tutorId || undefined } : null;
  const preview = useAdjustmentPreview(request.id, draft);
  const candidates = useAdjustmentCandidates(
    request.id,
    action === 'teacher' ? rangeInput : action === 'lesson' ? lessonInput : null,
  );
  const lessons = preview.data?.lessons ?? [];
  const changedCount = lessons.filter((l) => l.changed).length;
  const conflictCount = lessons.filter((l) => l.conflict).length;
  const shownTeachers = (candidates.data ?? [])
    .filter((t) => !t.current || action === 'lesson')
    .filter((t) => t.tutorName.toLowerCase().includes(teacherSearch.trim().toLowerCase()));
  const ready = Boolean(draft && preview.data?.canApply && reason.trim() && agreed && !apply.isPending);

  return (
    <Modal
      lang="ar"
      dir="rtl"
      className="learning-plans-ar"
      open
      width="lg"
      title="إدارة الخطة"
      description={`${request.studentName} · ${request.offer.nameAr || request.offer.nameEn}`}
      onClose={onClose}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink-2" role="status">
            {preview.data?.canApply
              ? `ستتغيّر ${changedCount} حصص، ويصل إشعار للمعلّم والأسرة.`
              : draft && conflictCount
                ? `${conflictCount} حصص بها تعارض. عدّل الاختيار قبل التطبيق.`
                : 'اختر التغيير لعرض الحصص المتأثرة.'}
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose}>إغلاق</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!ready}
              onClick={() => draft && apply.mutate(
                { id: request.id, input: { ...draft, reason: reason.trim() } },
                { onSuccess: () => onDone('تم تطبيق التغيير، وأُرسل إشعار للمعلّم والأسرة.') },
              )}
            >
              {apply.isPending ? 'جارٍ التطبيق…' : 'تطبيق التغيير'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {change && (
          <div className="rounded-lg border border-rule bg-surface-2 p-3 text-sm">
            <p className="font-medium">طلب الأسرة · {planLabel(`change.${change.kind}`)}</p>
            <p className="mt-1 text-ink-2">{change.reason}</p>
          </div>
        )}

        <section aria-label="الجدول الحالي" className="rounded-lg border border-rule p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <Avatar name={schedule?.tutorName || '—'} />
              <div>
                <p className="text-xs text-ink-3">المعلّم الأساسي</p>
                <p className="font-semibold">{schedule?.tutorName || '—'}</p>
              </div>
            </div>
            {tracks.length > 1 ? (
              <label className="text-sm">المسار
                <select className="input mt-1" value={trackKey} onChange={(e) => switchTrack(e.target.value)}>
                  {tracks.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                </select>
              </label>
            ) : (
              <Badge>{track?.name}</Badge>
            )}
          </div>
          <ul className="mt-3 flex flex-wrap gap-2" aria-label="المواعيد الأسبوعية">
            {schedule?.times.map((t) => (
              <li key={`${t.weekday}-${t.startLocal}`} className="rounded-md bg-surface-2 px-2.5 py-1 text-sm tabular-nums">
                {weekdayNames[t.weekday]} {t.startLocal}–{t.endLocal}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-ink-3">
            بتوقيت الرياض · {upcoming.length} حصص قادمة مؤكدة
            {balance && ` · تعديلات الطالب ${balance.changesUsed}/${balance.changesAllowed}`}
          </p>
        </section>

        <Step number={1} title="ماذا تريد أن تغيّر؟">
          <fieldset className="grid gap-2 sm:grid-cols-3">
            <legend className="sr-only">نوع التغيير</legend>
            {actions.map((a) => (
              <label key={a.value} className={optionCard}>
                <input type="radio" name="plan-action" className="sr-only" checked={action === a.value}
                  onChange={() => { setAction(a.value); setTutorId(''); setAgreed(false); apply.reset(); }} />
                <span className="mt-0.5 text-accent">{a.icon}</span>
                <span>
                  <span className="block text-sm font-semibold">{a.title}</span>
                  <span className="block text-xs text-ink-2">{a.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </Step>

        <Step number={2} title={action === 'times' ? 'المواعيد الجديدة' : action === 'teacher' ? 'المعلّم والفترة' : 'الحصة والتعديل'}>
          <div className="space-y-4">
            {action !== 'lesson' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">يسري التغيير ابتداءً من
                  <input className="input mt-1" type="date" value={effectiveFrom} min={firstDate}
                    onChange={(e) => edit(setEffectiveFrom)(e.target.value)} />
                  <span className="mt-1 block text-xs text-ink-3">الحصص قبل هذا التاريخ تبقى كما هي.</span>
                </label>
                {action === 'teacher' && !permanent && (
                  <label className="block text-sm">آخر يوم للمعلّم البديل
                    <input className="input mt-1" type="date" value={until} min={effectiveFrom}
                      onChange={(e) => { edit(setUntil)(e.target.value); setTutorId(''); }} />
                    <span className="mt-1 block text-xs text-ink-3">بعده يعود المعلّم الأساسي تلقائيًا.</span>
                  </label>
                )}
              </div>
            )}

            {action === 'times' && (
              <fieldset className="space-y-3">
                <legend className="text-sm font-medium">
                  {track?.weeklyMeetings} حصص أسبوعيًا · {track?.durationMinutes} دقيقة لكل حصة
                </legend>
                {times.map((t, index) => {
                  const was = currentTimes.find((x) => x.row === t.row);
                  const moved = was && (was.weekday !== t.weekday || was.startLocal !== t.startLocal);
                  return (
                    <div key={t.row} className="rounded-lg border border-rule p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium">الحصة {index + 1}</span>
                        {was && (
                          <span className="text-xs text-ink-3">
                            {moved ? <>كانت <span className="line-through">{weekdayNames[was.weekday]} {was.startLocal}</span></> : 'بدون تغيير'}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap items-end gap-3">
                        <fieldset>
                          <legend className="sr-only">اليوم للحصة {index + 1}</legend>
                          <div className="flex flex-wrap gap-1">
                            {weekdayNames.map((name, day) => (
                              <label key={name}
                                className="relative cursor-pointer rounded-md border border-rule px-2 py-1 text-xs has-[:checked]:border-accent has-[:checked]:bg-accent-wash has-[:checked]:font-semibold has-[:checked]:text-accent has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent">
                                <input type="radio" className="sr-only" name={`weekday-${t.row}`} checked={t.weekday === day}
                                  onChange={() => edit(setTimes)(times.map((x) => (x.row === t.row ? { ...x, weekday: day } : x)))} />
                                {name}
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        <label className="text-sm">وقت البداية بتوقيت الرياض
                          <input className="input mt-1 w-32" type="time" value={t.startLocal}
                            onChange={(e) => edit(setTimes)(times.map((x) => (x.row === t.row ? { ...x, startLocal: e.target.value } : x)))} />
                        </label>
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-ink-3">تحتفظ كل حصة بيومها وأسبوعها عند الإمكان. الحصة التي نُقلت سابقًا بشكل منفرد تبقى في موعدها.</p>
              </fieldset>
            )}

            {action === 'teacher' && (
              <fieldset className="grid gap-2 sm:grid-cols-2">
                <legend className="sr-only">مدة التغيير</legend>
                <label className={optionCard}>
                  <input type="radio" name="teacher-mode" className="sr-only" checked={permanent}
                    onChange={() => { edit(setPermanent)(true); setTutorId(''); }} />
                  <span>
                    <span className="block text-sm font-semibold">معلّم أساسي جديد</span>
                    <span className="block text-xs text-ink-2">لبقية الدورة، ويستمر معه عند التجديد</span>
                  </span>
                </label>
                <label className={optionCard}>
                  <input type="radio" name="teacher-mode" className="sr-only" checked={!permanent}
                    onChange={() => { edit(setPermanent)(false); setTutorId(''); }} />
                  <span>
                    <span className="block text-sm font-semibold">معلّم بديل مؤقت</span>
                    <span className="block text-xs text-ink-2">لفترة محددة، ثم يعود المعلّم الأساسي</span>
                  </span>
                </label>
              </fieldset>
            )}

            {action === 'lesson' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">الحصة
                  <select className="input mt-1" value={lessonId} onChange={(e) => { edit(setLessonId)(e.target.value); setTutorId(''); }}>
                    {upcoming.map((l) => <option key={l.id} value={l.id}>{when(l.startTime)} · {l.tutorName}</option>)}
                  </select>
                </label>
                <label className="block text-sm">الموعد الجديد بتوقيت الرياض
                  <input className="input mt-1" type="datetime-local" value={lessonStart}
                    onChange={(e) => { edit(setLessonStart)(e.target.value); setTutorId(''); }} />
                  <span className="mt-1 block text-xs text-ink-3">اتركه فارغًا لتغيير المعلّم فقط. لا يُحتسب من رصيد تعديلات الطالب.</span>
                </label>
              </div>
            )}

            {action !== 'times' && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">
                  {action === 'teacher' ? 'المعلّم الجديد' : 'معلّم بديل لهذه الحصة (اختياري)'}
                </legend>
                <p className="text-xs text-ink-3">يُعيَّن المعلّم مباشرة ويصله إشعار بالحصص، دون انتظار موافقته. المتاحون لكل الحصص أولًا.</p>
                {(candidates.data?.length ?? 0) > 6 && (
                  <input className="input" value={teacherSearch} placeholder="ابحث باسم المعلّم" aria-label="البحث عن معلّم"
                    onChange={(e) => setTeacherSearch(e.target.value)} />
                )}
                {!candidates.data && !candidates.isError && (
                  <p className="text-sm text-ink-3">{rangeInput || lessonInput ? 'جارٍ البحث عن المعلّمين المتاحين…' : 'حدّد الفترة أولًا.'}</p>
                )}
                {candidates.isError && <p role="alert" className="text-sm text-danger">{learningPlanError(candidates.error, 'تعذّر تحميل المعلّمين. أعد المحاولة.')}</p>}
                {candidates.data && shownTeachers.length === 0 && (
                  <p className="rounded-lg border border-dashed border-rule p-3 text-sm text-ink-2">لا يوجد معلّمون مؤهلون لهذه الحصص. جرّب فترة أخرى أو موعدًا مختلفًا.</p>
                )}
                <div className="max-h-64 space-y-2 overflow-y-auto">
                  {action === 'lesson' && candidates.data && (
                    <label className={optionCard}>
                      <input type="radio" name="plan-tutor" className="sr-only" checked={!tutorId} onChange={() => edit(setTutorId)('')} />
                      <span className="text-sm">الإبقاء على المعلّم الحالي</span>
                    </label>
                  )}
                  {shownTeachers.map((t) => (
                    <label key={t.tutorId} data-tutor-id={t.tutorId} className={optionCard}>
                      <input type="radio" name="plan-tutor" className="sr-only" checked={tutorId === t.tutorId}
                        onChange={() => edit(setTutorId)(t.tutorId)} />
                      <Avatar name={t.tutorName} size="sm" />
                      <span className="flex flex-1 flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium">{t.tutorName}{t.current ? ' (الحالي)' : ''}</span>
                        {t.available
                          ? <Badge tone="good">متاح لكل الحصص</Badge>
                          : <Badge tone="danger">غير متاح لـ {t.conflicts} حصص</Badge>}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
          </div>
        </Step>

        <Step number={3} title="معاينة الحصص المتأثرة">
          <section aria-label="معاينة التغيير" className="space-y-3">
            {!draft && <p className="rounded-lg border border-dashed border-rule p-4 text-center text-sm text-ink-2">اختر التغيير لعرض الحصص التي ستتأثر قبل الحفظ.</p>}
            {draft && preview.isLoading && <p className="text-sm text-ink-3">جارٍ التحقق من المواعيد…</p>}
            {draft && preview.isError && <p role="alert" className="text-sm text-danger">{learningPlanError(preview.error, 'تعذّرت المعاينة. راجع البيانات وحاول مرة أخرى.')}</p>}
            {draft && preview.data && (
              <>
                <div className="flex flex-wrap gap-2">
                  <Badge tone="accent">ستتغيّر {changedCount} من {lessons.length} حصص</Badge>
                  {lessons.length - changedCount > 0 && <Badge>بدون تغيير {lessons.length - changedCount}</Badge>}
                  {conflictCount > 0 && <Badge tone="danger">تعارض {conflictCount}</Badge>}
                </div>
                <p className="text-xs text-ink-3">
                  لا يتغيّر عدد الحصص المشتراة ولا رصيد تعديلات الطالب.
                  {preview.data.permanent && ' سيصبح المعلّم الجديد المعلّم الأساسي لهذا المسار.'}
                </p>
                <PreviewWeeks lessons={lessons} startDate={request.startDate} />
              </>
            )}
          </section>
        </Step>

        <Step number={4} title="التأكيد">
          <div className="space-y-3">
            <label className="block text-sm">سبب التغيير
              <textarea className="input mt-1" rows={2} value={reason} maxLength={1000}
                placeholder="مثال: طلبت الأسرة موعدًا مسائيًا" onChange={(e) => setReason(e.target.value)} />
            </label>
            <label className={`${optionCard} ${preview.data?.canApply ? '' : 'pointer-events-none opacity-60'}`}>
              <input type="checkbox" className="mt-0.5" checked={agreed} disabled={!preview.data?.canApply}
                onChange={(e) => setAgreed(e.target.checked)} />
              <span className="text-sm">
                <span className="block font-medium">وافقت الأسرة على هذا التغيير</span>
                <span className="block text-xs text-ink-2">يُسجَّل في سجل العمليات مع السبب.</span>
              </span>
            </label>
            {apply.isError && <p role="alert" className="text-sm text-danger">{learningPlanError(apply.error)}</p>}
          </div>
        </Step>
      </div>
    </Modal>
  );
}

function Step({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <span aria-hidden="true" className="inline-flex size-6 items-center justify-center rounded-full bg-surface-2 text-xs tabular-nums text-ink-2">
          {number}
        </span>
        {title}
      </h3>
      {children}
    </section>
  );
}

/** The affected lessons grouped by plan week, old time struck through beside the new one. */
function PreviewWeeks({ lessons, startDate }: { lessons: PlanAdjustmentLesson[]; startDate?: string }) {
  const weekOf = (iso: string) =>
    startDate ? Math.floor((Date.parse(riyadhDate(iso)) - Date.parse(startDate)) / (7 * 86400000)) + 1 : 1;
  const weeks = [...new Set(lessons.map((l) => weekOf(l.fromStartTime)))];
  return (
    <div className="max-h-80 space-y-3 overflow-y-auto">
      {weeks.map((week) => (
        <div key={week}>
          <p className="mb-1 text-xs font-medium text-ink-3">{week > 4 ? 'بعد نهاية الدورة' : `الأسبوع ${week}`}</p>
          <ul className="divide-y divide-rule rounded-lg border border-rule">
            {lessons.filter((l) => weekOf(l.fromStartTime) === week).map((l) => {
              const retimed = l.fromStartTime !== l.toStartTime;
              const reteached = l.fromTutorId !== l.toTutorId;
              const sameDay = riyadhDate(l.fromStartTime) === riyadhDate(l.toStartTime);
              return (
                <li key={l.lessonId} data-lesson-id={l.lessonId} className="grid gap-1 px-3 py-2 text-sm sm:grid-cols-[1fr_auto] sm:items-center">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="font-medium">{dayName(l.toStartTime)} {riyadhDate(l.toStartTime)}</span>
                    {retimed ? (
                      <span className="tabular-nums">
                        <span className="text-ink-3 line-through">{sameDay ? clock(l.fromStartTime) : when(l.fromStartTime)}</span>
                        {' ← '}
                        <span className="font-semibold">{clock(l.toStartTime)}</span>
                      </span>
                    ) : (
                      <span className="tabular-nums text-ink-2">{clock(l.fromStartTime)}</span>
                    )}
                    <span className="text-ink-2">
                      · {reteached ? <><span className="text-ink-3 line-through">{l.fromTutorName}</span> ← {l.toTutorName}</> : l.toTutorName}
                    </span>
                  </div>
                  <div>
                    {l.conflict ? <Badge tone="danger">تعارض</Badge>
                      : l.kept ? <Badge>منقولة سابقًا، تبقى</Badge>
                      : l.changed ? <Badge tone="accent">تتغيّر</Badge>
                      : <Badge>بدون تغيير</Badge>}
                  </div>
                  {l.conflict && (
                    <p className="flex items-start gap-1 text-xs text-danger sm:col-span-2">
                      <IconAlert className="mt-0.5 size-3.5 shrink-0" />
                      تعارض: {planLabel(l.conflict)}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
